import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { find, filter, includes } from 'lodash';
import * as countryData from './country_data'; 
import './flags/styles.scss'; 

const useStyles = makeStyles((theme) => ({
  flag: {
    // Removed the explicit width here. 
    // The SCSS file handles the correct width (16px) for the sprite.
    display: 'inline-block',
    marginRight: theme.spacing(1),
  },
  menuItemText: {
    display: 'flex',
    alignItems: 'center',
  },
  selectValue: {
    display: 'flex',
    alignItems: 'center',
  }
}));


// adapted logic taken from phone input component
const MaterialUiCountrySelect = (props) => {
  const classes = useStyles();
  const {
    regions,
    excludeCountries,
    onlyCountries,
    value,
    onChange,
    disabled,
    label,
    error,
    helperText,
    required,
    ...rest
  } = props;

  // ---  Filter Countries Logic ---
  const filteredCountries = useMemo(() => {
    let output = countryData.allCountries;

    // Filter by Region (e.g., 'europe')
    if (regions) {
      if (typeof regions === 'string') {
        output = output.filter((c) => c.regions.includes(regions));
      } else {
        output = output.filter((c) => regions.some(r => c.regions.includes(r)));
      }
    }

    // Filter by 'onlyCountries' whitelist
    if (onlyCountries && onlyCountries.length > 0) {
      output = output.filter((c) => onlyCountries.includes(c.iso2));
    }

    // Filter by 'excludeCountries' blacklist
    if (excludeCountries && excludeCountries.length > 0) {
      output = filter(output, (c) => !includes(excludeCountries, c.iso2));
    }

    return output;
  }, [regions, onlyCountries, excludeCountries]);

  // ---  Render Custom Selected Value (Flag + Dial Code) ---
  const renderValue = (selectedIso) => {
    // If no value is selected yet, return empty string (or placeholder)
    if (!selectedIso) return '';

    const country = find(filteredCountries, { iso2: selectedIso });
    if (!country) return selectedIso;

    return (
      <div className={classes.selectValue}>
        {/* The flag class from SCSS will now control the width correctly */}
        <div className={`flag ${country.iso2} ${classes.flag}`} />
        <span>+{country.dialCode}</span>
      </div>
    );
  };

  return (
    <TextField
      select
      label={label}
      value={value || ''}
      onChange={(e) => onChange && onChange(e.target.value)}
      disabled={disabled}
      error={error}
      helperText={helperText}
      required={required}
      fullWidth
      variant="filled"
      SelectProps={{
        renderValue: renderValue,
        MenuProps: {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left"
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left"
          },
          getContentAnchorEl: null
        }
      }}
      {...rest}
    >
      {filteredCountries.map((country) => (
        <MenuItem key={country.iso2} value={country.iso2}>
          <ListItemIcon style={{ minWidth: 40 }}>
            <div className={`flag ${country.iso2}`} />
          </ListItemIcon>
          <Typography variant="inherit" noWrap>
            {country.name} (+{country.dialCode})
          </Typography>
        </MenuItem>
      ))}
    </TextField>
  );
};

MaterialUiCountrySelect.propTypes = {
  regions: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  value: PropTypes.string,
  onChange: PropTypes.func,
};

MaterialUiCountrySelect.defaultProps = {
  regions: '',
  excludeCountries: [],
  onlyCountries: [],
};

export default MaterialUiCountrySelect;